import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  cancelFeedback,
  hideFeedbackSuccess,
  setBg,
  setCourseIndex,
  setDrawer,
  startFeedback,
  submitFeedback,
  submitUserQuery,
  togglePlaying
} from "../store/chatSlice";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import "./ChatBox.css";

const ChatBox = () => {
  const dispatch = useAppDispatch();
  const { conversations, showBg, showFeedback, showFeedbackSuccess, feedbackAnswerId, showDrawer } = useAppSelector((state) => state.chat);
  const [feedbackText, setFeedbackText] = useState("");
  const [showCopyToast, setShowCopyToast] = useState(false);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const carouselRefs = useRef<Array<HTMLDivElement | null>>([]);
  const latestConversation = conversations[conversations.length - 1];
  const previousConversation = conversations[conversations.length - 2];

  useEffect(() => {
    if (!chatContainerRef.current || !latestConversation) {
      return;
    }

    if (latestConversation.type === "user" || (latestConversation.typing && previousConversation?.type === "user")) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth"
      });
      return;
    }

    if (latestConversation.typing) {
      return;
    }

    chatContainerRef.current.scrollBy({
      top: 240,
      behavior: "smooth"
    });
  }, [latestConversation?.time, latestConversation?.type, latestConversation?.typing, previousConversation?.type]);

  useEffect(() => {
    if (!showFeedbackSuccess) {
      return;
    }
    const t = window.setTimeout(() => {
      dispatch(hideFeedbackSuccess());
    }, 2000);
    return () => window.clearTimeout(t);
  }, [showFeedbackSuccess, dispatch]);

  const canSubmitFeedback = useMemo(() => feedbackText.trim().length > 99, [feedbackText]);

  return (
    <>
      {showCopyToast ? (
        <div className="copy-toast">
          <span className="copy-toast-icon">
            <i className="fa-solid fa-circle-info" />
          </span>
          <span className="copy-toast-text">Message copied!</span>
        </div>
      ) : null}
      {(showFeedbackSuccess || showFeedback) ? (
        <div className="feedback-overlay">
          <div className="feedbackarea">
            {showFeedbackSuccess ? (
              <>
                <h1>Thank You</h1>
                <i className="fa-solid fa-thumbs-up" style={{ fontSize: "42px", color: "#3c6ab5" }} />
              </>
            ) : (
              <>
                <h1>Apologies for any inconvenience.</h1>
                <p>We value your feedback as it helps us in continuous improvement.</p>
                <textarea cols={30} placeholder="Type-in here (min. 100 characters)..." rows={5} onChange={(event) => setFeedbackText(event.target.value)} />
                <div className="fb-buttons">
                  <button onClick={() => { dispatch(cancelFeedback()); dispatch(setBg(false)); }}>Close</button>
                  <button
                    disabled={!canSubmitFeedback}
                    onClick={async () => {
                      if (feedbackAnswerId === null) {
                        return;
                      }
                      await dispatch(submitFeedback({ id: feedbackAnswerId, text: feedbackText, like: "false" }));
                    }}
                  >
                    Submit
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      <div className="chat" ref={chatContainerRef}>
        {conversations.map((conversation, i) => (
          <div key={`${conversation.time}-${i}`} className={`conversation ${conversation.type === "user" ? "user" : "bot"}`}>
            {conversation.type === "bot" ? <img src="/fav.png" alt="" /> : null}
            <div className={`message-container ${conversation.type === "user" ? "user-container" : ""}`}>
              <div className={`message-part ${conversation.type === "user" ? "user-message-part" : ""}`}>
                <div className={`message-text ${conversation.type === "user" ? "user-text" : ""}`}>
                  {conversation.typing ? (
                    <div className="typing">
                      <div className="typing__dot" />
                      <div className="typing__dot" />
                      <div className="typing__dot" />
                    </div>
                  ) : (
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                      a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />
                    }}>{(() => {
                      if (!conversation.query) return "";
                      return conversation.query.replace(/\p{Extended_Pictographic}/gu, "");
                    })()}</ReactMarkdown>
                  )}
                  {conversation.ask_for_resume || (conversation.show_download && !conversation.brochureLink) || conversation.brochureLink ? (
                    <div className="buttons">
                      {conversation.ask_for_resume ? (
                        <button className="upload-btn" onClick={() => { dispatch(setDrawer(true)); dispatch(setBg(true)); }}>
                          <i className="fa-solid fa-cloud-arrow-up" />
                          <span> Upload resume </span>
                        </button>
                      ) : null}
                      {conversation.show_download && !conversation.brochureLink ? (
                        <button className="upload-btn" onClick={() => void dispatch(submitUserQuery({ query: "Yes", inputType: "TEXT" }))}>
                          <i className="fa-solid fa-cloud-arrow-down" />
                          <span> Download brochure </span>
                        </button>
                      ) : null}
                      {conversation.brochureLink ? (
                        <button className="upload-btn" onClick={() => window.open(conversation.brochureLink ?? "", "_blank")}>
                          <i className="fa-solid fa-cloud-arrow-down" />
                          <span> Download </span>
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  {conversation.courses && conversation.courses.length > 0 ? (
                    <div className="carousel-container">
                      <div className="carousel-wrapper" ref={(element) => { carouselRefs.current[i] = element; }}>
                        {conversation.courses.map((course, courseIndex) => (
                          <div className="course" key={`${course.course_name}-${courseIndex}`}>
                            <span className="course_img"><img src="/course.jpeg" alt="" /></span>
                            <span className="course_title">{course.course_name}</span>
                            <span className="course_desc">{course.course_description}</span>
                            <button className="course_button" onClick={async () => {
                              const query = course.course_name;
                              await dispatch(submitUserQuery({ query, inputType: "TEXT" }));
                            }}>Read more</button>
                          </div>
                        ))}
                      </div>
                      <div className="carousel-controls">
                        <button className="nav-button" onClick={() => {
                          const target = carouselRefs.current[i];
                          if (target) {
                            target.scrollBy({ left: -220, behavior: "smooth" });
                          }
                          dispatch(setCourseIndex({ convoIndex: i, activeIndex: Math.max((conversation.activeIndex ?? 0) - 1, 0) }));
                        }}>
                          <i className="fa-solid fa-angle-left icon" />
                        </button>
                        <div className="dots">
                          {conversation.courses.map((_, dotIndex) => (
                            <span key={dotIndex} className={conversation.activeIndex === dotIndex ? "active" : ""} onClick={() => dispatch(setCourseIndex({ convoIndex: i, activeIndex: dotIndex }))} />
                          ))}
                        </div>
                        <button className="nav-button" onClick={() => {
                          const target = carouselRefs.current[i];
                          if (target) {
                            target.scrollBy({ left: 220, behavior: "smooth" });
                          }
                          dispatch(setCourseIndex({ convoIndex: i, activeIndex: Math.min((conversation.activeIndex ?? 0) + 1, (conversation.courses?.length ?? 1) - 1) }));
                        }}>
                          <i className="fa-solid fa-angle-right icon" />
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              {!conversation.typing ? (
                <div className="message-footer message-footer-bot">
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", color: "#858585" }}>
                    {conversation.type === "bot" ? (
                      <i
                        className={conversation.isplaying ? "fa-solid fa-volume-xmark" : "fa-solid fa-volume-high"}
                        onClick={() => {
                          if (!audioPlayerRef.current || !conversation.audio) {
                            return;
                          }
                          
                          if (conversation.isplaying) {
                            audioPlayerRef.current.pause();
                            dispatch(togglePlaying(i));
                          } else {
                            // If another audio was playing, it might still be playing if we don't pause it,
                            // but the single audio element naturally handles this when we change src.
                            dispatch(togglePlaying(i));
                            audioPlayerRef.current.src = conversation.audio;
                            void audioPlayerRef.current.play();
                            
                            audioPlayerRef.current.onended = () => {
                              dispatch(togglePlaying(i));
                            };
                          }
                        }}
                      />
                    ) : null}
                    {conversation.type === "bot" ? (
                      <i
                        className="fa-regular fa-copy"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(conversation.query);
                            setShowCopyToast(true);
                            setTimeout(() => setShowCopyToast(false), 1500);
                          } catch (error) {
                            console.error("Failed to copy text", error);
                          }
                        }}
                      />
                    ) : null}
                    {conversation.type === "bot" ? (
                      <i
                        className="fa-regular fa-thumbs-up"
                        onClick={() => {
                          const id = conversation.answerId ?? "";
                          dispatch(startFeedback({ id, like: true }));
                          void dispatch(submitFeedback({ id, text: "", like: "true" }));
                        }}
                      />
                    ) : null}
                    {conversation.type === "bot" ? (
                      <i
                        className="fa-regular fa-thumbs-down fa-flip-horizontal"
                        onClick={() => {
                          const id = conversation.answerId ?? "";
                          dispatch(startFeedback({ id, like: false }));
                          dispatch(setBg(true));
                        }}
                      />
                    ) : null}
                  </div>
                  <span className="chat-time">Just now</span>
                </div>
              ) : null}
            </div>
          </div>
        ))}
        <audio ref={audioPlayerRef}>
          <source src="" type="audio/mpeg" />
        </audio>
      </div>
    </>
  );
};

export default ChatBox;
